/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  DoCheck,
  Host,
  Inject,
  Input,
  IterableChangeRecord,
  IterableChanges,
  IterableDiffer,
  IterableDiffers,
  OnDestroy,
  OnInit,
  TemplateRef,
  TrackByFunction,
  ViewContainerRef,
  ViewRef,
} from '@angular/core';

import { onAnimationFrame$ } from '@gh/core-util';
import { combineLatest$, merge$ } from '@gh/rx';
import { distinctUntilChanged$, first$, map$, share$, skip$, tap$ } from '@gh/rx/operators';

import { find, isEqual, isNil, isNumber } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';

import { VirtualItemComponent } from './virtual-item.component';
import { VirtualListComponent } from './virtual-list.component';
import {
  constantVirtualItemSizeStrategy, expandRange,
  isFitInVirtualViewport, isRangeContains,
  VIRTUAL_CONTAINER_PROVIDER,
  VirtualContainer,
  VirtualContainerProvider,
  VirtualItemSizeStrategy,
  VirtualRange,
  VirtualViewport,
} from './virtual-rendering.interfaces';
import { VirtualViewportDirective } from './virtual-viewport.directive';

const VIRTUAL_BUFFER_SIZE = 5;
const EMPTY_RENDERED_RANGE = { start: 0, end: 0 };

const isEmptyRange = (range: VirtualRange) => range.start === range.end;

const identityTrackByFn: TrackByFunction<any> = (_, data) => data;

/**
 * This directive renders only currently visible items in the viewport.
 * Dimensions and position of items and list defined via provided {@link VirtualItemSizeStrategy}.
 *
 * Note. Substantial part of this class implementation were taken from ngForOf/cdkVirtualFor.
 */
@Directive({
  standalone: false,
  selector: '[ghVirtualListFor][ghVirtualListForOf]',
})
export class VirtualListForDirective<T> implements OnInit, OnDestroy, DoCheck {

  @Input('ghVirtualListForTrackBy') trackBy: TrackByFunction<T> = identityTrackByFn;
  @Input('ghVirtualListCacheSize') cacheSize: number = 10;

  private _itemSize: VirtualItemSizeStrategy<T>;

  private renderedRange: { start: number; end: number } = EMPTY_RENDERED_RANGE;
  private renderedItems: T[] = [];
  private differ?: IterableDiffer<T>;
  private items$$ = new BehaviorSubject<T[]>([]);
  private _dangledItem?: T;
  private itemSizeChanged$$ = new BehaviorSubject<void>(void 0);
  private subscription: Subscription;
  private needsUpdate = false;
  private renderedComponents: ComponentRef<VirtualItemComponent>[] = [];
  private cachedComponents: ComponentRef<VirtualItemComponent>[] = [];

  @Input('ghVirtualListForOf')
  set items(items: T[]) {
    this.items$$.next(items);
  }

  get items(): T[] {
    return this.items$$.value;
  }

  @Input('ghVirtualListDangledItem')
  set dangledItem(item: Maybe<T>) {
    this._dangledItem = item;
  }

  get dangledItem(): Maybe<T> {
    return this._dangledItem;
  }

  @Input('ghVirtualListForItemSize')
  set itemSize(itemSize: number | VirtualItemSizeStrategy<T>) {
    this._itemSize = isNumber(itemSize) ? constantVirtualItemSizeStrategy(itemSize) : itemSize;
    this._itemSize.registerOnSizeChange(this.onItemSizeChanged.bind(this));
  }

  constructor(private differs: IterableDiffers,
              private viewContainerRef: ViewContainerRef,
              private templateRef: TemplateRef<any>,
              private changeDetectorRef: ChangeDetectorRef,
              @Host() private list: VirtualListComponent,
              @Inject(VIRTUAL_CONTAINER_PROVIDER) private virtualContainerProvider: VirtualContainerProvider,
              private viewport: VirtualViewportDirective) {
  }

  ngOnInit(): void {
    // range of items to render
    const visibleRange$ = combineLatest$(
      this.viewport.viewport,
      this.virtualContainerProvider.container,
      this.items$$,
      this.itemSizeChanged$$).pipe(
      tap$(() => {
        const fullSize = this._itemSize.calculateFullSize(this.items);
        this.virtualContainerProvider.setDimensions(
          this.list.orientation === 'horizontal' ? fullSize : void 0,
          this.list.orientation === 'vertical' ? fullSize : void 0);
      }),
      map$(([viewport]) => [viewport, this.virtualContainerProvider.containerSnapshot]),
      map$(([viewport, container]) => {
        if (isFitInVirtualViewport(viewport, container)) {
          return this.getVisibleRange(viewport, container);
        } else {
          return EMPTY_RENDERED_RANGE;
        }
      }),
      distinctUntilChanged$(isEqual),
      share$());

    // initial render will happen immediately
    const init$ = visibleRange$.pipe(first$());
    // next renders will happen at most one time in animated frame
    const change$ = combineLatest$(visibleRange$, this.items$$, this.itemSizeChanged$$).pipe(
      (onAnimationFrame$),
      skip$(1),
      map$(([range]) => range));

    this.subscription = merge$(init$, change$).subscribe((range) => this.render(range));

    // this.render(this.viewport.viewportSnapshot);
  }

  ngOnDestroy(): void {
    this.renderedComponents = [];
    this.items$$.complete();
    this.itemSizeChanged$$.complete();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngDoCheck(): void {
    if (this.differ && this.needsUpdate) {
      const changes = this.differ.diff(this.renderedItems);
      if (changes) {
        this.applyChanges(changes);
      } else {
        this.updateContext();
        this.updateOffsets();
      }
      this.needsUpdate = false;
    }
  }

  private onItemSizeChanged(): void {
    // trigger recalculation of rendered range when size of item is changed
    this.itemSizeChanged$$.next(void 0);
  }

  private render(range: VirtualRange): void {
    if (isEmptyRange(range) && isEmptyRange(this.renderedRange)) {
      return;
    }

    this.renderedRange = isRangeContains(this.renderedRange, range) ?
      this.renderedRange
      : expandRange(range, this.items.length, VIRTUAL_BUFFER_SIZE);
    this.renderedItems = this.buildRenderedItems(this.items.slice(this.renderedRange.start, this.renderedRange.end));
    this.needsUpdate = true;
    this.ensureDiffer();
    this.changeDetectorRef.markForCheck();
  }

  private ensureDiffer(): IterableDiffer<T> {
    if (isNil(this.differ)) {
      this.differ = this.differs.find(this.renderedItems).create(this.trackBy);
    }
    return this.differ;
  }

  private updateContext(): void {
    this.renderedItems.forEach((item, index) => {
      const virtualItem = this.renderedComponents[index];
      if (virtualItem) {
        virtualItem.instance.data = item;
      }
    });
  }

  private updateOffsets(): void {
    // if tail has dangled item we should not update its position
    const renderedRangeLength = this.renderedRange.end - this.renderedRange.start;
    for (let i = 0; i < renderedRangeLength; i++) {
      const virtualItem = this.renderedComponents[i];
      if (virtualItem) {
        this.calculateItemBounds(virtualItem, i);
      }
    }
  }

  private applyChanges(changes: IterableChanges<T>): void {
    changes.forEachOperation((record: IterableChangeRecord<T>,
                              adjustedPreviousIndex: number,
                              currentIndex: number) => {
      if (isNil(record.previousIndex)) {
        const virtualItem = this.takeView(currentIndex);
        virtualItem.instance.data = record.item;
        this.renderedComponents.splice(currentIndex, 0, virtualItem);
      } else if (isNil(currentIndex)) {
        this.viewContainerRef.detach(adjustedPreviousIndex);
        this.releaseView(this.renderedComponents[adjustedPreviousIndex]);
        this.renderedComponents.splice(adjustedPreviousIndex, 1);
      } else {
        const view = <ViewRef>this.viewContainerRef.get(adjustedPreviousIndex);
        this.viewContainerRef.move(view, currentIndex);
        const virtualItem = this.renderedComponents[adjustedPreviousIndex];
        virtualItem.instance.data = record.item;
        this.renderedComponents.splice(adjustedPreviousIndex, 1);
        this.renderedComponents.splice(currentIndex, 0, virtualItem);
      }
    });

    changes.forEachIdentityChange((record: IterableChangeRecord<T>) => {
      const virtualItem = this.renderedComponents[record.currentIndex!];
      virtualItem.instance.data = record.item;
    });

    this.updateOffsets();
  }

  private getVisibleRange(viewport: VirtualViewport, container: VirtualContainer): VirtualRange {
    const pxRange = this.list.orientation === 'vertical'
      ? {
        start: Math.max(Math.max(viewport.top, container.top) - container.top, 0),
        end: Math.max(Math.min(viewport.bottom, container.bottom) - container.top, 0),
      }
      : {
        start: Math.max(Math.max(viewport.left, container.left) - container.left, 0),
        end: Math.max(Math.min(viewport.right, container.right) - container.left, 0),
      };
    return this._itemSize.calculateVisibleRange(pxRange, this.items);
  }

  private calculateItemBounds(virtualItem: ComponentRef<VirtualItemComponent>, index: number): void {
    const component = virtualItem.instance;
    if (this.list.orientation === 'vertical') {
      component.top = this._itemSize.calculatePosition(component.data, this.renderedRange.start + index);
    } else if (this.list.orientation === 'horizontal') {
      component.left = this._itemSize.calculatePosition(component.data, this.renderedRange.start + index);
    }
  }

  /**
   * Builds list of rendered items:
   * if dangled item should be displayed we add it to the tail of visible items.
   * @param itemsToRender
   */
  private buildRenderedItems(itemsToRender: T[]): T[] {
    const { dangledItem, trackBy } = this;
    // find dangled item in list of visible items using provided trackBy function
    if (dangledItem && isNil(find(itemsToRender, (item, i) => trackBy(i, item) === trackBy(-1, dangledItem)))) {
      return itemsToRender.concat([dangledItem]);
    } else {
      return itemsToRender;
    }
  }

  /** Cache the given detached view. */
  private releaseView(component: ComponentRef<VirtualItemComponent>): void {
    if (this.cachedComponents.length < this.cacheSize) {
      this.cachedComponents.push(component);
    } else {
      const index = this.viewContainerRef.indexOf(component.hostView);

      // It's very unlikely that the index will ever be -1, but just in case,
      // destroy the view on its own, otherwise destroy it through the
      // container to ensure that all the references are removed.
      if (index === -1) {
        component.destroy();
      } else {
        this.viewContainerRef.remove(index);
      }
    }
  }

  /** Get a view for a new item, either from the cache or by creating a new one. */
  private takeView(index: number): ComponentRef<VirtualItemComponent> {
    if (this.cachedComponents.length) {
      const virtualItem = this.cachedComponents.pop()!;
      this.viewContainerRef.insert(virtualItem.hostView, index);
      return virtualItem;
    } else {
      const virtualItem = this.viewContainerRef.createComponent(VirtualItemComponent, { index });
      virtualItem.instance.template = this.templateRef;
      return virtualItem;
    }
  }
}
